import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type PresenceOfRapidCyclesStringEnum = "yes" | "no";

export default class DisorderHistoryDeclaredJournalEvent extends JournalEvent {
  static type = "disorder-history-declared-journal-event";

  @attr("number") declare numberOfDepressiveEpisode: number;

  @attr("number") declare numberOfHypomaniacEpisode: number;

  @attr("number") declare numberOfManiacEpisode: number;

  @attr("string") declare numberOfEpisodeCommentary: string;

  @attr("number") declare numberOfHospitalization: number;

  @attr("number") declare numberOfTs: number;

  @attr("number") declare numberOfTsWithThymicEpisode: number;

  @attr("string") declare numberOfTsCommentary: string;

  @attr("string")
  declare presenceOfRapidCycles: PresenceOfRapidCyclesStringEnum;

  @attr("string") declare presenceOfRapidCyclesCommentary: string;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.disorder-history-declared-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      numberOfDepressiveEpisode: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.disorder-history-declared-journal-event.number-of-depressive-episode"
        )
      }),
      numberOfHypomaniacEpisode: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.disorder-history-declared-journal-event.number-of-hypomaniac-episode"
        )
      }),
      numberOfManiacEpisode: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.disorder-history-declared-journal-event.number-of-maniac-episode"
        )
      }),
      numberOfHospitalization: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.disorder-history-declared-journal-event.number-of-hospitalization"
        )
      }),
      numberOfTs: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.disorder-history-declared-journal-event.number-of-ts"
        )
      }),
      numberOfTsWithThymicEpisode: validateNumber({
        allowBlank: true,
        integer: true,
        description: intl.t(
          "validations.disorder-history-declared-journal-event.number-of-ts-with-thymic-episode"
        )
      }),
      presenceOfRapidCycles: validateInclusion({
        list: ["yes", "no"],
        allowBlank: true,
        description: intl.t(
          "validations.disorder-history-declared-journal-event.presence-of-rapid-cycles"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "disorderHistoryDeclaredJournalEvent"
  })
  declare patient: Patient;

  @attr("thymic-episodes") declare thymicEpisodes;

  get countThymicEpisodesAllLife(): number {
    return (
      this.numberOfDepressiveEpisode +
      this.numberOfHypomaniacEpisode +
      this.numberOfManiacEpisode
    );
  }

  get predominance(): string {
    const predominanceThreshold = (2 / 3) * this.countThymicEpisodesAllLife;

    if (this.numberOfDepressiveEpisode >= predominanceThreshold) {
      return "dépressive";
    } else if (
      this.numberOfHypomaniacEpisode + this.numberOfManiacEpisode >=
      predominanceThreshold
    ) {
      return "maniaque";
    }
    return "indifférenciée";
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "disorder-history-declared-journal-event": DisorderHistoryDeclaredJournalEvent;
  }
}
